import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTitulos } from '../actions/titulosActions';
import { createCertificado } from '../actions/socioActions';
import QRCode from 'qrcode';

// Guardia global por sesión para evitar múltiples creates por socio/tipo
const emittedSessionKeys = new Set();

const CertificadoSolvencia = ({ show, handleClose, socio, textoCertificado }) => {
  const dispatch = useDispatch();
  const { titulos } = useSelector(state => state.titulo);

  const [gradoTexto, setGradoTexto] = useState('ingeniero');

  // Nuevos: estado para URL/código y PDF
  const [isSaving, setIsSaving] = useState(false);
  const [certUrl, setCertUrl] = useState('');
  const [certCodigo, setCertCodigo] = useState('');

  // 1) Cargar títulos del socio
  useEffect(() => {
    const sid = socio?.id || socio?._id || socio?.idsocio;
    if (sid) dispatch(fetchTitulos(sid));
  }, [dispatch, socio]);

  // 2) Calcular gradoTexto
  useEffect(() => {
    if (titulos && titulos.length > 0) {
      const grados = titulos.map(t => t.grado);
      if (grados.every(g => g === 10)) setGradoTexto('técnico');
      else if (grados.includes(1)) setGradoTexto('ingeniero');
      else setGradoTexto('ingeniero');
    }
  }, [titulos]);

  // 3) Limpiar visor al cerrar modal
  useEffect(() => {
    if (!show) {
      setIsSaving(false);
      setCertUrl('');
      setCertCodigo('');
      const container = document.getElementById('pdf-preview');
      if (container) container.innerHTML = '';
    }
  }, [show]);

  const loadImage = async (url) => {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    return arrayBuffer;
  };

  // Extraer código desde una URL /ver-certificado/:codigo
  const extractCodeFromUrl = (url) => {
    try {
      if (!url) return '';
      const u = new URL(url, window.location.origin);
      const segs = u.pathname.replace(/\/+$/,'').split('/');
      return decodeURIComponent(segs[segs.length - 1] || '');
    } catch {
      const clean = (url || '').split('?')[0].split('#')[0];
      const parts = clean.split('/');
      return decodeURIComponent(parts.pop() || '');
    }
  };

  // JSON de respaldo para QR si no hay URL pública
  const buildQRPayload = useCallback(() => {
    const hoyISO = new Date().toISOString().slice(0, 10);
    const titulosLimpios = Array.isArray(titulos)
      ? titulos.map(t => ({
          grado: t.grado,
          titulo: t.titulo || t.nombre || '',
          universidad: t.suniv || t.universidad || socio?.suniv || '',
          nrotit: t.nrotit || socio?.nrotit || '',
          fecha: t.fecha || t.fechatitulo || ''
        }))
      : [];

    return JSON.stringify({
      entidad: 'CIAB - Filial Tarija',
      tipo: 'Certificado de Solvencia',
      generado: hoyISO,
      socio: {
        id: socio?.id || socio?._id || socio?.idsocio,
        codreg: socio?.codreg,
        ci: socio?.ci,
        nombres: socio?.nombres,
        apPat: socio?.apPat,
        apMat: socio?.apMat,
        genero: socio?.genero,
        gradoTexto,
        nrotit: socio?.nrotit,
        suniv: socio?.suniv,
        telefono: socio?.telefono || socio?.celular || '',
        email: socio?.email || socio?.correo || '',
      },
      titulos: titulosLimpios,
      textoCertificado: textoCertificado || ''
    });
  }, [socio, gradoTexto, titulos, textoCertificado]);

  // 4) Grabar en backend y obtener URL/Código
  const emitirYObtenerUrl = useCallback(async () => {
    const socioId = socio?.id || socio?._id || socio?.idsocio;
    if (!socioId) {
      toast.error('Faltan datos del socio (id).');
      return { url: null, codigo: null };
    }
    try {
      setIsSaving(true);

      const data = await dispatch(
        createCertificado({
          socio,
          tipoUi: 'SOLVENCIA',
          titulos,
          gradoTexto,
          textoCertificado
        })
      );

      const codigo = data?.certificado?.codigo || '';
      if (codigo) setCertCodigo(codigo);

      if (codigo) {
        const urlFront = `${window.location.origin}/ver-certificado/${encodeURIComponent(codigo)}`;
        setCertUrl(urlFront);
        toast.success('Certificado de solvencia creado correctamente.');
        return { url: urlFront, codigo };
      }

      const hint = data?.public_url_hint || '';
      const abs = hint
        ? (hint.startsWith('http') ? hint : `${window.location.origin}${hint}`)
        : '';

      if (abs) {
        setCertUrl(abs);
        if (!codigo) setCertCodigo(extractCodeFromUrl(abs));
        toast.success('Certificado de solvencia creado correctamente.');
        return { url: abs, codigo: codigo || extractCodeFromUrl(abs) || '' };
      }

      toast.success('Certificado de solvencia creado (sin URL pública).');
      return { url: null, codigo: codigo || '' };
    } catch (e) {
      console.error('[emitirYObtenerUrl] Error:', e);
      toast.error('No se pudo registrar el certificado en el servidor.');
      return { url: null, codigo: null };
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, socio, titulos, gradoTexto, textoCertificado]);

  // === PDF (misma maqueta/estilo) + CÓDIGO bajo el título + QR abajo-izquierda ===
  const generatePDF = useCallback(async (qrContentParam, codigoParam) => {
    try {
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([612, 792]);

      const marginLeft = 113; // 4 cm
      const marginRight = 85; // 3 cm
      const maxWidth = page.getWidth() - marginLeft - marginRight;
      const fontSize = 12;

      const logoBytes = await loadImage('/assets/cabeceraciab.png');
      const pieBytes  = await loadImage('/assets/pieciab.png');
      const logoImage = await pdfDoc.embedPng(logoBytes);
      const pieImage  = await pdfDoc.embedPng(pieBytes);
      const logoDims  = logoImage.scale(0.35);
      const pieDims   = pieImage.scale(0.35);

      page.drawImage(logoImage, {
        x: marginLeft,
        y: page.getHeight() - logoDims.height - 40,
        width: logoDims.width,
        height: logoDims.height,
      });

      page.drawImage(pieImage, {
        x: marginLeft,
        y: 20,
        width: pieDims.width,
        height: pieDims.height,
      });

      const font     = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const today = new Date();
      const day   = today.getDate();
      const month = today.toLocaleString('es-ES', { month: 'long' });
      const year  = today.getFullYear();

      const title  = 'CERTIFICADO';
      const yTitle = page.getHeight() - logoDims.height - 80;

      page.drawText(title, {
        x: (page.getWidth() - fontBold.widthOfTextAtSize(title, 16)) / 2,
        y: yTitle,
        size: 16,
        font: fontBold,
        color: rgb(0, 0, 0),
      });

      // NUEVO: Código bajo el título, centrado
      const codeToRender = codigoParam || certCodigo || extractCodeFromUrl(certUrl) || '—';
      const codeText = `Código: ${codeToRender}`;
      const codeWidth = font.widthOfTextAtSize(codeText, 12);
     /* page.drawText(codeText, {
        x: (page.getWidth() - codeWidth) / 2,
        y: yTitle - 18,
        size: 12,
        font,
        color: rgb(0, 0, 0),
      });*/

      let y = yTitle - 60;

      const ella = socio.genero === 'F' ? 'la' : 'el';
      const habi = socio.genero === 'F' ? 'habilitada' : 'habilitado';
      const insc = socio.genero === 'F' ? 'inscrita' : 'inscrito';
      const acti = socio.genero === 'F' ? 'activa' : 'activo';

      const lineaNombre = [
        { texto: `Que, ${ella} ${gradoTexto}: `, bold: false },
        { texto: `${socio.apPat} ${socio.apMat} ${socio.nombres}`, bold: true },
        { texto: `, con C.I. ${socio.ci}, Título en Provisión Nacional N° ${socio.nrotit}, ${socio.suniv || ''}, se encuentra legalmente ${insc} y ${acti} en el CIAB - FILIAL TARIJA, con el registro ${socio.codreg}, encontrándose ${habi} para ejercer la profesión (Ley N° 37014).`, bold: false }
      ];

      const procesarTextoCertificado = (texto) => {
        return (texto || '')
          .split('\n')
          .map(line => line.trim())
          .filter(line => line !== '')
          .map(line => {
            const partes = line.split(/(\*.*?\*)/g);
            return partes.map(p => ({
              texto: p.replace(/\*/g, ''),
              bold: p.startsWith('*') && p.endsWith('*')
            }));
          });
      };

      const justificarSegmentos = (segmentos) => {
        const lineas = [];
        let lineaActual = [];
        let anchoActual = 0;

        const flushLine = (justify) => {
          if (lineaActual.length === 0) return;
          const totalTextWidth = lineaActual.reduce((sum, seg) => {
            const f = seg.bold ? fontBold : font;
            return sum + f.widthOfTextAtSize(seg.texto, fontSize);
          }, 0);
          const espacios = lineaActual.filter(seg => seg.texto.trim() === '').length;
          const extraSpace = (justify && espacios > 0)
            ? (maxWidth - totalTextWidth) / espacios
            : 0;
          lineas.push({ linea: [...lineaActual], extraSpace });
          lineaActual = [];
          anchoActual = 0;
        };

        segmentos.forEach(seg => {
          const f = seg.bold ? fontBold : font;
          const tokens = seg.texto.split(/(\s+)/);
          tokens.forEach(token => {
            if (token === '') return;
            const w = f.widthOfTextAtSize(token, fontSize);
            if (anchoActual + w > maxWidth && token.trim() !== '') {
              flushLine(true);
            }
            lineaActual.push({ ...seg, texto: token });
            anchoActual += w;
          });
        });

        flushLine(false);
        return lineas;
      };

      const addFormattedText = (segmentos, yPos) => {
        const lineas = justificarSegmentos(segmentos);
        lineas.forEach(({ linea, extraSpace }) => {
          let x = marginLeft;
          linea.forEach(seg => {
            const f = seg.bold ? fontBold : font;
            page.drawText(seg.texto, {
              x,
              y: yPos,
              size: fontSize,
              font: f,
              color: rgb(0, 0, 0),
            });
            x += f.widthOfTextAtSize(seg.texto, fontSize);
            if (seg.texto.trim() === '' && extraSpace > 0) x += extraSpace;
          });
          yPos -= 16;
        });
        return yPos;
      };

      y = addFormattedText(lineaNombre, y);
      y -= 20;

      const lineaValidez = [
        { texto: 'Este certificado es válido para:', bold: false }
      ];
      y = addFormattedText(lineaValidez, y);
      y -= 16;

      const textoProcesado = procesarTextoCertificado(textoCertificado);
      textoProcesado.forEach(segmentos => {
        y = addFormattedText(segmentos, y);
        y -= 8;
      });

      const lineaValidez1 = [
        { texto: 'Tiempo de validez del presente certificado: ', bold: false },
        { texto: '30 días.', bold: true }
      ];
      y = addFormattedText(lineaValidez1, y);
      y -= 14;

      const lineaCertificamos = [
        { texto: 'Es cuanto certificamos en honor a la verdad y para los fines consiguientes del interesado.', bold: false }
      ];
      y = addFormattedText(lineaCertificamos, y);
      y -= 14;

      const ciudad = socio?.ciudad || 'Tarija';
      const fecha = `${ciudad}, ${day} de ${month.charAt(0).toUpperCase() + month.slice(1)} de ${year}`;
      y -= 10;
      page.drawText(fecha, {
        x: page.getWidth() - marginRight - font.widthOfTextAtSize(fecha, fontSize),
        y,
        size: fontSize,
        font,
        color: rgb(0, 0, 0),
      });

      // === QR abajo-izquierda (sobre el pie) ===
      const contentForQR = qrContentParam || certUrl || buildQRPayload();
      const qrDataUrl = await QRCode.toDataURL(contentForQR, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 180,
      });
      const qrRes = await fetch(qrDataUrl);
      const qrBytes = await qrRes.arrayBuffer();
      const qrImage = await pdfDoc.embedPng(qrBytes);

      const qrScale = 140 / qrImage.width; // ~140pt ancho
      const qrDims  = qrImage.scale(qrScale);

      const padding = 16;
      const qrX = marginLeft;                  // alineado con margen izquierdo original
      const qrY = 20 + pieDims.height + padding;

     /* page.drawText('Escanear para verificar', {
        x: qrX,
        y: qrY + qrDims.height + 8,
        size: 10,
        font,
        color: rgb(0, 0, 0),
      });*/

      page.drawImage(qrImage, {
        x: qrX,
        y: qrY,
        width: qrDims.width,
        height: qrDims.height,
      });

      const pdfDataUri = await pdfDoc.saveAsBase64({ dataUri: true });
      const iframe = `<iframe width='100%' height='600px' src='${pdfDataUri}'></iframe>`;
      const container = document.getElementById('pdf-preview');
      if (container) container.innerHTML = iframe;
    } catch (error) {
      console.error(error);
      toast.error("Error al generar el PDF. Por favor verifique los datos del socio...");
      handleClose();
    }
  }, [buildQRPayload, socio, certUrl, certCodigo, gradoTexto, textoCertificado]);

  // === Al abrir modal: emitir UNA SOLA VEZ por sesión (tipo SOLVENCIA) y luego generar el PDF ===
  useEffect(() => {
    if (!show || !socio) return;
    const socioId = socio?.id || socio?._id || socio?.idsocio;
    if (!socioId) return;

    const key = `SOLVENCIA:${socioId}`;
    if (emittedSessionKeys.has(key)) {
      // Ya emitido en esta sesión: sólo re-render del PDF con lo que haya
      (async () => {
        const code = certCodigo || extractCodeFromUrl(certUrl) || '';
        await generatePDF(certUrl || undefined, code || undefined);
      })();
      return;
    }

    emittedSessionKeys.add(key);

    (async () => {
      const { url, codigo } = await emitirYObtenerUrl();
      await generatePDF(url || undefined, codigo || undefined);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, socio]);

  return (
    <Modal show={show} onHide={handleClose} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
      <Modal.Header closeButton>
        <Modal.Title>Certificado de Solvencia</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div id="pdf-preview"></div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CertificadoSolvencia;
